import React, { useState } from "react";
import { Box, Tabs, Tab, Typography, Stack, TextField, InputAdornment, Chip } from "@mui/material";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";

const BASE_PATH = "/influencer/brand-deals";

// Shared design tokens — a deliberately distinct identity for Affiliate Deals
// (warm paper background, teal accent, per-category color coding) rather
// than the app's default neutral palette, so it reads as its own product
// area — a marketplace — instead of a generic settings-style page.
export const BD = {
  paper: "#f5f6f1",
  card: "#ffffff",
  border: "#dedcd2",
  borderHover: "#c6c2b2",
  textPrimary: "#16241f",
  textSecondary: "#4c5c54",
  surfaceMuted: "#eeece2",
  accent: "#0d6b5c",
  accentDark: "#0a4f45",
  accentWash: "#e3f0ec",
  coral: "#ff6b4a",
  coralWash: "#ffe9e2",
  radiusLg: "18px",
  radiusMd: "14px",
  radiusSm: "10px",
  fontDisplay: "'Space Grotesk', sans-serif",
  fontBody: "'IBM Plex Sans', sans-serif",
};

// ClanConnect's actual brand tokens (see src/assets/styles/helpers/_variable.scss
// and the app-wide Open Sans body font) — now used across the whole Brand
// Deals surface (Campaigns, this layout's chrome, CampaignDetail,
// PreviewDialog, CashkaroLinks, CashkaroEarnings) plus its admin/brand-side
// views (PublisherManager, StorefrontCashkaroPerformance) so they all read
// as part of ClanConnect rather than the one-off warm-paper/teal theme above.
// `BD` above is left untouched only for AddCampaignProductDialog.
export const CC = {
  bg: "#f9fafc",
  card: "#ffffff",
  border: "#E5E7EB",
  borderHover: "#c7cad1",
  textPrimary: "#1a1a1a",
  textSecondary: "#6b7280",
  primary: "#00aae5",
  primaryDark: "#0090c4",
  primaryWash: "#e6f7fd",
  accent: "#ee1b6c",
  accentWash: "#fde7ef",
  radiusLg: "16px",
  radiusMd: "12px",
  radiusSm: "10px",
  font: "'Open Sans', sans-serif",
};

// Deterministic color per category name (Trackier categories are freeform
// strings, not a fixed enum) — same category always gets the same color
// without needing to hardcode every possible value Trackier might send.
const CATEGORY_PALETTE = ["#d1477a", "#2a5db0", "#c07a1f", "#5b5bd6", "#b8548f", "#1a8fa6", "#3f8f5f", "#a3572b"];
export function categoryColor(name?: string | null): string {
  if (!name) return BD.accent;
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  return CATEGORY_PALETTE[hash % CATEGORY_PALETTE.length];
}

// Best-effort emoji per category, matched by keyword since Trackier category names are
// freeform strings, not a fixed enum (e.g. "banking and finance", "miscellaneousness") —
// substring match against common categories, falling back to a generic tag icon for
// anything unmatched rather than guessing wrong.
const CATEGORY_ICON_RULES: { keywords: string[]; icon: string }[] = [
  { keywords: ["fashion", "apparel", "footwear", "cloth"], icon: "👗" },
  { keywords: ["bank", "finance", "credit", "loan", "card", "insurance"], icon: "🏦" },
  { keywords: ["food", "grocery", "restaurant"], icon: "🍽️" },
  { keywords: ["electronic", "gadget", "mobile", "computer", "audio"], icon: "🎧" },
  { keywords: ["beauty", "personal care", "skincare", "cosmetic"], icon: "💄" },
  { keywords: ["travel", "hotel", "flight", "tour"], icon: "🧳" },
  { keywords: ["health", "wellness", "pharma", "medicine"], icon: "💊" },
  { keywords: ["sport", "fitness", "gym"], icon: "🏋️" },
  { keywords: ["baby", "kid"], icon: "🍼" },
  { keywords: ["home", "kitchen", "furniture"], icon: "🛋️" },
  { keywords: ["education", "learning"], icon: "🎓" },
  { keywords: ["gift"], icon: "🎁" },
  { keywords: ["entertainment", "gaming", "game"], icon: "🎮" },
  { keywords: ["jewel"], icon: "💍" },
  { keywords: ["pet"], icon: "🐾" },
];
export function categoryIcon(name?: string | null): string {
  const n = (name || "").toLowerCase();
  for (const { keywords, icon } of CATEGORY_ICON_RULES) {
    if (keywords.some((k) => n.includes(k))) return icon;
  }
  return "🏷️";
}

// Shared between CampaignDetail and PreviewDialog — both render the same
// campaign-summary anatomy (payout chip, tracking/validation meta line,
// terms-accordion header), so those pieces live here once instead of being
// hand-copied per call site.

// Payout figures must never render for a campaign flagged hide_payout — see
// normalizeCampaign rule 6 in the backend; callers are responsible for that
// check before rendering this.
export const PayoutChip: React.FC<{ label: string; height?: number }> = ({ label, height = 22 }) => (
  <Chip
    icon={<LocalOfferOutlinedIcon sx={{ fontSize: "13px !important", color: `${CC.accent} !important` }} />}
    label={label}
    size="small"
    sx={{
      height,
      fontSize: 11,
      fontWeight: 700,
      fontFamily: CC.font,
      borderRadius: "999px",
      bgcolor: CC.accentWash,
      color: CC.accent,
      "& .MuiChip-icon": { ml: "6px" },
    }}
  />
);

// "Tracking window" / "Validation" line shown under a campaign's title —
// icon-labeled and divided rather than joined by a plain " · " so the two
// figures scan independently.
export const CampaignMetaRow: React.FC<{
  trackingWindow?: string | null;
  validationDays?: number | null;
  iconSize?: number;
  sx?: Record<string, unknown>;
}> = ({ trackingWindow, validationDays, iconSize = 14, sx }) => {
  if (!trackingWindow && !validationDays) return null;
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", ...sx }}>
      {trackingWindow && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
          <AccessTimeOutlinedIcon sx={{ fontSize: iconSize, color: CC.textSecondary }} />
          <Typography variant="caption" sx={{ color: CC.textSecondary, fontFamily: CC.font }}>
            Tracking window: {trackingWindow}
          </Typography>
        </Box>
      )}
      {trackingWindow && validationDays ? <Box sx={{ width: "1px", height: 12, bgcolor: CC.border }} /> : null}
      {validationDays ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
          <VerifiedOutlinedIcon sx={{ fontSize: iconSize, color: CC.textSecondary }} />
          <Typography variant="caption" sx={{ color: CC.textSecondary, fontFamily: CC.font }}>
            Validation: {validationDays} day{validationDays === 1 ? "" : "s"}
          </Typography>
        </Box>
      ) : null}
    </Box>
  );
};

// Icon + label content for the "Campaign terms" accordion header. Only this
// inner content is shared — the Accordion/AccordionSummary chrome around it
// differs enough between CampaignDetail (top-border only) and PreviewDialog
// (full bordered box) that the wrapper itself stays local to each caller.
export const CampaignTermsHeader: React.FC = () => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
    <CampaignOutlinedIcon sx={{ fontSize: 16, color: CC.primary }} />
    <Typography variant="body2" fontWeight={600} sx={{ color: CC.textPrimary, fontFamily: CC.font }}>
      Campaign terms
    </Typography>
  </Box>
);

// terms_html is the sanitized campaign `kpi` field (see campaignNormalizer.ts)
// — brands type it as one rule per line, which sanitize-html turns into
// sibling <p> tags (or, less often, a real <ul>/<li> list). Styled here as
// bullets covering both shapes, rather than as a browser-default list, so a
// colored marker (not the raw HTML) drives how each rule reads.
export const termsListSx = {
  "& p, & li": {
    position: "relative" as const,
    listStyle: "none",
    margin: 0,
    marginBottom: "0.6em",
    paddingLeft: "1.1em",
    "&:last-child": { marginBottom: 0 },
  },
  "& p::before, & li::before": {
    content: '"•"',
    position: "absolute" as const,
    left: 0,
    color: CC.primary,
    fontWeight: 700,
  },
  "& ul": { margin: 0, padding: 0 },
};

const TABS = [
  { label: "Campaigns", path: BASE_PATH },
  { label: "My Links", path: `${BASE_PATH}/links` },
  { label: "Earnings", path: `${BASE_PATH}/earnings` },
];

// Per-tab search fields (Campaigns, My Links) are owned here — not in the
// route components themselves — so each field can sit on the same row as the
// tabs, to their right, instead of inside the page body. Handed down to
// whichever route is active via Outlet context; each route only reads its own
// slice. `linksCount` flows the other way — CashkaroLinks reports how many
// (filtered) links it has so the tab row can show "N links" next to its
// search box without the layout needing to know about link data itself.
export interface CashkaroOutletContext {
  search: string;
  setSearch: (value: string) => void;
  linksSearch: string;
  setLinksSearch: (value: string) => void;
  linksCount: number | null;
  setLinksCount: (count: number) => void;
}

// Shell for the influencer-facing affiliate module: browse brand campaigns,
// request access, generate trackable links, and track earnings — all scoped
// to the logged-in influencer via their sub_id (see backend Cashkaro
// controller; internal naming there is unrelated to what's shown here).
export const CashkaroLayout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [linksSearch, setLinksSearch] = useState("");
  const [linksCount, setLinksCount] = useState<number | null>(null);

  const activeTab = TABS.reduce((best, tab, index) => {
    if (location.pathname === tab.path || (tab.path !== BASE_PATH && location.pathname.startsWith(tab.path))) {
      return index;
    }
    return best;
  }, 0);
  const isCampaignsTab = activeTab === 0;
  const isLinksTab = activeTab === 1;

  return (
    <Box sx={{minHeight: "100%" }}>
      <Box sx={{ maxWidth: 1200, mx: "auto", px: { xs: 2, sm: 3, md: 4 } }}>
        <Box display="flex" alignItems="center" gap={{ xs: 1.25, lg: 2 }} flexWrap="wrap" mb={{ xs: 0.5, sm: 2 }}>
          <Box
            sx={{
              width: { xs: 44, sm: 60 },
              height: { xs: 44, sm: 60 },
              borderRadius: "10px",
              flexShrink: 0,
              border: "1px solid",
              borderColor: CC.border,
              bgcolor: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <LocalOfferOutlinedIcon sx={{ fontSize: { xs: 20, sm: 28 }, color: CC.primary }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: { xs: 16, md: 22 }, fontWeight: 700, color: CC.textPrimary, fontFamily: CC.font }}>
              Affiliate Deals
            </Typography>
            <Typography sx={{ color: CC.textSecondary, fontSize: { xs: 12, md: 15 }, mt: { xs: 0.25, sm: 0 }, fontFamily: CC.font }}>
              Discover brand campaigns, generate trackable links, and track what you earn.
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: { xs: "stretch", sm: "center" },
            justifyContent: "space-between",
            flexWrap: { xs: "wrap", sm: "nowrap" },
            gap: 1.25,
            mb: 2.5,
          }}
        >
          <Tabs
            value={activeTab}
            onChange={(_, index) => navigate(TABS[index].path)}
            variant="fullWidth"
            sx={{
              minHeight: 34,
              bgcolor: "#ececf0",
              borderRadius: "999px",
              p: "6px",
              width: { xs: "100%", sm: "auto" },
              minWidth: { sm: 360 },
              maxWidth: 448,
              flexShrink: 0,
              "& .MuiTabs-indicator": { display: "none" },
            }}
          >
            {TABS.map((tab) => (
              <Tab
                key={tab.path}
                label={tab.label}
                sx={{
                  minHeight: 28,
                  textTransform: "none",
                  fontSize: 13,
                  fontWeight: 500,
                  fontFamily: CC.font,
                  borderRadius: "999px",
                  color: CC.textPrimary,
                  lineHeight: 1,
                  px: 1.5,
                  minWidth: 0,
                  "&:hover": { bgcolor: "#000", color: "#fff" },
                  "&.Mui-selected": { bgcolor: "#000", color: "#fff", fontWeight: 600 },
                }}
              />
            ))}
          </Tabs>

          {isCampaignsTab && (
            <TextField
              size="small"
              placeholder="Search brand campaigns"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{
                width: { xs: "100%", sm: 240, md: 280 },
                flexShrink: 0,
                mb: { xs: 1, sm: 0 },
                bgcolor: CC.card,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "999px",
                  fontFamily: CC.font,
                  "& fieldset": { borderColor: CC.border },
                  "&:hover fieldset": { borderColor: CC.borderHover },
                  "&.Mui-focused fieldset": { borderColor: CC.primary },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon fontSize="small" sx={{ color: CC.textSecondary }} />
                  </InputAdornment>
                ),
              }}
            />
          )}

          {isLinksTab && (
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.25}
              alignItems={{ xs: "stretch", sm: "center" }}
              sx={{ width: { xs: "100%", sm: "auto" }, flexShrink: 0, mb: { xs: 1, sm: 0 } }}
            >
              {linksCount !== null && (
                <Typography sx={{ fontFamily: CC.font, fontWeight: 700, fontSize: "0.875rem", color: CC.textPrimary, whiteSpace: "nowrap" }}>
                  {linksCount} {linksCount === 1 ? "link" : "links"}
                </Typography>
              )}
              <TextField
                size="small"
                placeholder="Search links..."
                value={linksSearch}
                onChange={(e) => setLinksSearch(e.target.value)}
                sx={{
                  width: { xs: "100%", sm: 220, md: 260 },
                  bgcolor: CC.card,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "999px",
                    fontFamily: CC.font,
                    "& fieldset": { borderColor: CC.border },
                    "&:hover fieldset": { borderColor: CC.borderHover },
                    "&.Mui-focused fieldset": { borderColor: CC.primary },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRoundedIcon fontSize="small" sx={{ color: CC.textSecondary }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>
          )}
        </Box>

        <Outlet context={{ search, setSearch, linksSearch, setLinksSearch, linksCount, setLinksCount } satisfies CashkaroOutletContext} />
      </Box>
    </Box>
  );
};

export default CashkaroLayout;
